import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { BuyButton } from '../../components/Button'
import ProgressBar from '../../components/common/ProgressBar'
import { STATICS } from '../../static'
import { PoolInfo } from '../../hooks/usePoolInfo'
import useIGOStage, { IGOStage } from '../../hooks/useIGOStage'
import { commify, formatUnits } from 'ethers/lib/utils'
import Loading from '../../components/common/Loading'
import useHandleRebase from '../../hooks/useHandleRebase'
import useHandleDraw from '../../hooks/useHandleDraw'
import useIGOClaim from '../../hooks/useIGOClaim'
import { useCountDownData } from '../../hooks/useCountDown'
import useUserRaised from '../../hooks/useUserRaised'
import { useNavigate } from 'react-router-dom'
import { useClaimSection } from '../../hooks/useClaimSection'
import RecordModal from './RecordModal'
// import moment from 'moment'
import { transformToUTC } from '../../utils'

export default function Information({ poolInfo }: { poolInfo: PoolInfo | null }) {
  const stage = useIGOStage(poolInfo)

  const raised = useUserRaised(poolInfo?.address)
  const navigate = useNavigate()
  useEffect(() => {
    if (raised === false) {
      navigate('/launchpad', { replace: true })
    }
  }, [raised, navigate])
  return (
    <Wrapper>
      {stage === IGOStage.None && (
        <LoadingWrapper>
          <Loading height={100} />
        </LoadingWrapper>
      )}
      {stage <= IGOStage.Finish && stage !== IGOStage.None && <TimeLine poolInfo={poolInfo} stage={stage} />}
      {stage === IGOStage.DrawOut && <Fail poolInfo={poolInfo} />}
      {stage === IGOStage.DrawIn && <Success poolInfo={poolInfo} />}
      {/* {false && <AmountReceive poolInfo={poolInfo} />} */}
      <Selling poolInfo={poolInfo} />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  height: 343px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 42px;
`

function TimeLine({ stage, poolInfo }: { stage: IGOStage; poolInfo: PoolInfo | null }) {
  const handleRebase = useHandleRebase(poolInfo?.address)
  const handleDraw = useHandleDraw(poolInfo?.address)
  return (
    <TimeLineWrapper>
      <TimeLineTitle>Timeline</TimeLineTitle>
      <AmountTips>
        Amount purchased:{' '}
        {poolInfo && (
          <>
            {+formatUnits(poolInfo.oneShareAmount, poolInfo.targetToken.decimals)} {poolInfo.targetToken.symbol}
          </>
        )}
      </AmountTips>
      <ProcessWrapper>
        <ProcessItem done={stage >= IGOStage.OnSale}>
          <IndexItem>1</IndexItem>
          <IndexedTitle>Selling in progress</IndexedTitle>
        </ProcessItem>
        <ProcessItem done={stage >= IGOStage.ReBase}>
          <IndexItem>2</IndexItem>
          <IndexedTitle>Rebase</IndexedTitle>
        </ProcessItem>
        <ProcessItem done={stage >= IGOStage.Draw}>
          <IndexItem>3</IndexItem>
          <IndexedTitle>Confirming results</IndexedTitle>
        </ProcessItem>
        <ProcessItem done={stage >= IGOStage.Finish}>
          <IndexItem>4</IndexItem>
          <IndexedTitle>Claim</IndexedTitle>
        </ProcessItem>
        <ProcessItem done={stage >= IGOStage.Finish}>
          <IndexItem>5</IndexItem>
          <IndexedTitle>End</IndexedTitle>
        </ProcessItem>
      </ProcessWrapper>
      {stage === IGOStage.OnSale && poolInfo && <CountDown pooInfo={poolInfo} />}
      {(stage === IGOStage.ReBase || stage === IGOStage.Draw) && (
        <ButtonWrapper>
          {stage === IGOStage.ReBase ? (
            <RebaseButton onClick={handleRebase}>Rebase</RebaseButton>
          ) : (
            <RebaseButton onClick={handleDraw}>Result check</RebaseButton>
          )}
        </ButtonWrapper>
      )}
    </TimeLineWrapper>
  )
}

const RebaseButton = styled(BuyButton)`
  width: 284px;
  height: 50px;
  border-radius: 25px;
`

const TimeLineWrapper = styled.div`
  height: 343px;
  width: 546px;
  border-radius: 20px;
  background: #11388c;
  padding-top: 17px;
  overflow: hidden;
  position: relative;
`

const LoadingWrapper = styled(TimeLineWrapper)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  padding-top: 30px;
`
const TimeLineTitle = styled.h5`
  font-size: 18px;
  line-height: 25px;
  color: #fff;
  text-align: center;
`
const AmountTips = styled.p`
  font-size: 12px;
  color: #6090f8;
  line-height: 17px;
  text-align: center;
  margin-top: 11px;
`

const ProcessWrapper = styled.div`
  width: 100%;
  height: 70px;
  margin: 44px auto 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 80%;
    height: 2px;
    background: #2152bb;
    top: 17px;
    left: 55px;
  }
`

const IndexItem = styled.span`
  display: inline-block;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #2152bb;
  background-clip: content-box;
  border: solid 3px rgba(33, 82, 187, 0.4);
  text-align: center;
  color: #6090f8;
  font-size: 14px;
  font-weight: 600;
  line-height: 30px;
`
const IndexedTitle = styled.span`
  font-size: 12px;
  color: #6090f8;
  margin-top: 15px;
`

const ProcessItem = styled.div<{ done?: boolean }>`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  z-index: 9;

  ${IndexItem} {
    color: ${({ done }) => (done ? '#022981' : '#6090F8')};
    background-color: ${({ done }) => (done ? '#00F4E4' : '#2152BB')};
    border-color: ${({ done }) => (done ? 'rgba(0, 244, 244, 0.4)' : 'rgba(33,82,187, .4)')};
  }

  ${IndexedTitle} {
    color: ${({ done }) => (done ? '#00F4E4' : '#6090F8')};
  }
`

function CountDown(props: { pooInfo: PoolInfo }) {
  const { day, hour, min, sec } = useCountDownData(props.pooInfo.raisedEndTime)
  return (
    <CountDownWrapper>
      <CountDownTitle>Countdown to sale</CountDownTitle>
      <CountdownContent>
        <TimeItem>
          <span>{day}</span>
          <span>Days</span>
        </TimeItem>
        <Split>:</Split>
        <TimeItem>
          <span>{hour}</span>
          <span>Hours</span>
        </TimeItem>
        <Split>:</Split>
        <TimeItem>
          <span>{min}</span>
          <span>Minutes</span>
        </TimeItem>
        <Split>:</Split>
        <TimeItem>
          <span>{sec}</span>
          <span>Seconds</span>
        </TimeItem>
      </CountdownContent>
    </CountDownWrapper>
  )
}

const CountdownContent = styled.div`
  margin-top: 15px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`

const TimeItem = styled.div`
  height: 55px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;

  span:first-child {
    font-size: 24px;
    color: #fff;
    line-height: 29px;
    font-weight: bold;
  }

  span:last-child {
    font-size: 14px;
    color: #6090f8;
    line-height: 20px;
  }
`

const Split = styled.span`
  font-size: 18px;
  color: #6090f8;
  line-height: 25px;
  margin: 0 20px;
  text-align: center;
`

const CountDownWrapper = styled.div`
  width: 100%;
  height: 129px;
  position: absolute;
  bottom: 0;
  left: 0;
  padding-top: 19px;
  background: #0f3382;
`

const ButtonWrapper = styled(CountDownWrapper)`
  display: flex;
  justify-content: center;
  align-items: center;
`

const CountDownTitle = styled.h5`
  font-size: 14px;
  color: #6090f8;
  line-height: 20px;
  font-weight: normal;
  text-align: center;
`

function Fail({ poolInfo }: { poolInfo: PoolInfo | null }) {
  return (
    <FailWrapper>
      <Icon src={STATICS.FailIcon} />
      <Text>Sorry you missed it</Text>
      <Desc>
        Purchase amount: {poolInfo ? +formatUnits(poolInfo.oneSharePrice, poolInfo.costToken.decimals) : '0'}{' '}
        {poolInfo?.costToken.symbol}-has been returned to your wallet
      </Desc>
    </FailWrapper>
  )
}

const FailWrapper = styled.div`
  width: 565px;
  height: 343px;
  background: #11388c;
  border-radius: 20px;
  position: relative;
  padding-top: 97px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
`
const Icon = styled.img`
  width: 60px;
  height: 60px;
`
const Text = styled.h3`
  font-size: 18px;
  line-height: 25px;
  color: #fff;
  margin-top: 26px;
  text-align: center;
`
const Desc = styled.p`
  font-size: 12px;
  color: #6090f8;
  line-height: 17px;
  margin-top: 22px;
  text-align: center;
`

function Success({ poolInfo }: { poolInfo: PoolInfo | null }) {
  const { canClaim, handleClaim, claimAmount } = useIGOClaim(poolInfo?.address)
  const { releaseInfo, sections, claimedAmount } = useClaimSection(poolInfo?.address)

  const initialRate = useMemo(() => {
    if (!releaseInfo) return 0
    return (releaseInfo.firstRate * 100) / releaseInfo.INT_BASE
  }, [releaseInfo])
  const [show, setShow] = useState(false)
  return (
    <SuccessWrapper>
      <Icon src={STATICS.SuccessIcon} />
      <Text>Wow! You win!</Text>
      <StyledDesc style={{ visibility: 'hidden' }}>
        Winning Token: <span>12.12 TATA</span>
      </StyledDesc>
      <StyledDesc>
        Please claim the initial {initialRate}% token on{' '}
        {transformToUTC(releaseInfo?.firstTime || 0).format('hh:mma(UTC) MMM.DD.YYYY')}.
      </StyledDesc>
      <ReceiveButton disabled={!canClaim} onClick={handleClaim}>
        Claim
      </ReceiveButton>
      <StyledDesc style={{ marginTop: 20 }}>
        Remaining shares: {+formatUnits(claimAmount, poolInfo?.targetToken.decimals || 18)}{' '}
        {poolInfo?.targetToken.symbol}
      </StyledDesc>
      <RecordIcon src={STATICS.RecordIcon} onClick={() => setShow(true)} />
      {poolInfo && (
        <RecordModal
          poolInfo={poolInfo}
          show={show}
          onClose={() => setShow(false)}
          claimInfo={{ sections, claimedAmount }}
        />
      )}
    </SuccessWrapper>
  )
}

const RecordIcon = styled.img`
  width: 18px;
  height: 22px;
  position: absolute;
  top: 30px;
  right: 39px;
  cursor: pointer;
`

const SuccessWrapper = styled.div`
  width: 565px;
  height: 343px;
  background: #11388c;
  border-radius: 20px;
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  padding-top: 36px;
`
const StyledDesc = styled(Desc)`
  line-height: 21px;
  margin-top: 12px;
  span {
    color: #ffd244;
  }
`
// const Line = styled.span`
//   width: 100%;
//   height: 1px;
//   background: #2152BB;
//   margin-top: 34px;
// `

const ReceiveButton = styled(BuyButton)`
  width: 190px;
  height: 45px;
  border-radius: 23px;
  margin-top: 23px;
`

function Selling({ poolInfo }: { poolInfo: PoolInfo | null }) {
  // console.log(poolInfo)
  return (
    <SellingWrapper>
      {poolInfo === null ? (
        <Loading style={{ marginTop: 30 }} height={100} />
      ) : (
        <>
          <SellingTitle>Selling Information</SellingTitle>
          <SellingContent>
            <Tips>Total Sales</Tips>
            <AmountWrapper>
              <span>
                {commify(formatUnits(poolInfo.oneShareAmount.mul(poolInfo.totalShares), poolInfo.targetToken.decimals))}{' '}
                {poolInfo.targetToken.symbol}
              </span>
              <span>
                1 {poolInfo.targetToken.symbol} ={' '}
                {+formatUnits(poolInfo.oneSharePrice, poolInfo.costToken.decimals) /
                  +formatUnits(poolInfo.oneShareAmount, poolInfo.targetToken.decimals)}{' '}
                {poolInfo.costToken.symbol}
              </span>
            </AmountWrapper>
            <SaleInfo>
              <span>Total Participation</span>
              <span>
                ({+poolInfo.totalRaisedUser} / {poolInfo.totalShares})
              </span>
            </SaleInfo>
            <SaleProgress progress={+poolInfo.totalRaisedUser / poolInfo.totalShares} />
            <Tips style={{ marginTop: 20 }}>
              Cocos-BCX IGO is open for all COCOS members.
              {/*Based on the shares that users purchased, the rules are as follows at the end of IGO:*/}
              {/*Shares less than IGO amount: 100% winning rate for all users.*/}
              {/*Shares outnumber IGO amount: winning rate varies according to the VIP level. The higher level, the higher the winning rate.*/}
            </Tips>
          </SellingContent>
        </>
      )}
    </SellingWrapper>
  )
}

const SellingWrapper = styled.div`
  width: 546px;
  height: 343px;
  border-radius: 20px;
  background: linear-gradient(173deg, #11388c 0%, #11388c 100%);
  overflow: hidden;
`
const SellingTitle = styled.h3`
  height: 60px;
  line-height: 60px;
  text-align: center;
  font-size: 18px;
  color: #f3f3f3;
  font-weight: normal;
  border-bottom: solid 1px #2152bb;
`
const SellingContent = styled.div`
  padding: 50px 40px 0 40px;
  position: relative;
`
const Tips = styled.p`
  font-size: 14px;
  color: #6090f8;
  line-height: 20px;
`

const AmountWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 7px;

  span:first-child {
    font-size: 28px;
    color: #fff;
  }

  span:last-child {
    font-size: 14px;
    color: #6090f8;
  }
`
const SaleInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  margin-top: 43px;

  span:first-child {
    color: #fff;
  }

  span:last-child {
    color: #6090f8;
  }
`
const SaleProgress = styled(ProgressBar)`
  margin-top: 15px;
`

// function AmountReceive({ poolInfo }: { poolInfo: PoolInfo | null }) {
//   const { canClaim, handleClaim } = useIGOClaim(poolInfo?.address)
//   return (
//     <AmountReceiveWrapper>
//       <ReceiveTitle>12.12 TATA Phase II</ReceiveTitle>
//       <ReceiveText>Winning Token</ReceiveText>
//       <AmountReceiveButton disabled={canClaim} onClick={handleClaim}>
//         Receive
//       </AmountReceiveButton>
//       <Purchased>
//         Amount purchased: <span>12.12 COCOS</span>
//       </Purchased>
//       <StyledText>
//         20% will be issued in the first installment, and 10% will be released in each
//         <br /> installment after three months. Need you to collect
//       </StyledText>
//     </AmountReceiveWrapper>
//   )
// }

// const AmountReceiveWrapper = styled(SuccessWrapper)`
//   padding-top: 60px;
// `
// const ReceiveTitle = styled.h3`
//   font-size: 24px;
//   color: #ffd244;
//   font-weight: 500;
//   line-height: 33px;
//   text-align: center;
// `
// const ReceiveText = styled.p`
//   text-align: center;
//   font-size: 14px;
//   line-height: 20px;
//   color: #6090f8;
//   margin-top: 5px;
// `
// const AmountReceiveButton = styled(BuyButton)`
//   height: 45px;
//   border-radius: 23px;
//   width: 190px;
//   margin-top: 16px;
// `
// const Purchased = styled.p`
//   font-size: 12px;
//   color: #6090f8;
//   line-height: 17px;
//   text-align: center;
//   margin-top: 20px;
//   span {
//     color: #ffd244;
//   }
// `

// const StyledText = styled.p`
//   width: 508px;
//   height: 64px;
//   background: #18429c;
//   border-radius: 8px;
//   padding: 15px 38px;
//   font-size: 12px;
//   color: #6090f8;
//   line-height: 17px;
//   margin-top: 34px;
// `
